/**
 * Lists of domains for validation purposes
 */
export const FREE_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "protonmail.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
];

export const TEMP_EMAIL_DOMAINS = [
  "temp-mail.org",
  "guerrillamail.com",
  "mailinator.com",
  "10minutemail.com",
  "throwawaymail.com",
  "tempmail.com",
  "tempinbox.com",
  "yopmail.com",
  "dispostable.com",
  "sharklasers.com",
];

/**
 * Validates an email address format using regex
 * @param email Email address to validate
 * @returns Boolean indicating if email format is valid
 */
export const validateEmail = (email: string): boolean => {
  // RFC 5322 compliant email regex
  const emailRegex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRegex.test(email);
};

/**
 * Checks if an email contains plus addressing (username+tag@domain.com)
 * @param email Email address to check
 * @returns Boolean indicating if the email uses plus addressing
 */
export const hasEmailPlusAddressing = (email: string): boolean => {
  if (!email || !email.includes("@")) return false;

  const localPart = email.split("@")[0];
  return localPart.includes("+");
};

/**
 * Checks if an email is from a temporary/disposable email domain
 * @param email Email address to check
 * @returns Boolean indicating if it's a temporary email
 */
export const isTemporaryEmail = (email: string): boolean => {
  if (!email || !email.includes("@")) return false;

  const domain = email.split("@")[1].toLowerCase();
  return TEMP_EMAIL_DOMAINS.includes(domain);
};

/**
 * Checks if an email is from a free email domain
 * @param email Email address to check
 * @returns Boolean indicating if it's a free email
 */
export const isFreeEmail = (email: string): boolean => {
  if (!email || !email.includes("@")) return false;

  const domain = email.split("@")[1].toLowerCase();
  return FREE_EMAIL_DOMAINS.includes(domain);
};

/**
 * Validates a phone number format using international standards
 * @param phone Phone number to validate (with country code)
 * @returns Boolean indicating if phone format is valid
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  
  // Basic E.164 format validation: starts with + followed by 7-15 digits
  const e164Regex = /^\+[1-9]\d{6,14}$/;
  return e164Regex.test(phone);
};

/**
 * Validates an Indian phone number format (for backward compatibility)
 * @param phone Phone number to validate (with or without country code)
 * @returns Boolean indicating if phone format is valid
 */
export const validateIndianPhone = (phone: string): boolean => {
  if (!phone) return false;
  
  // Remove spaces, dashes, parentheses, and plus sign
  const cleanedPhone = phone.replace(/[\s\-()+]/g, "");

  // Remove leading '91' if present
  const normalizedPhone = cleanedPhone.startsWith("91") && cleanedPhone.length === 12
    ? cleanedPhone.slice(2)
    : cleanedPhone;

  // Now check if it's a valid 10-digit Indian number
  return /^[6-9]\d{9}$/.test(normalizedPhone);
};

/**
 * Formats a phone number to E.164 format if it's valid
 * @param phone Phone number to format
 * @returns Formatted phone number in E.164 format or original if invalid
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return "";
  
  // If already in valid E.164 format, return as is
  if (validatePhone(phone)) {
    return phone;
  }
  
  // For backward compatibility, try Indian formatting
  return formatIndianPhone(phone);
};

/**
 * Formats an Indian phone number to E.164 format (+91xxxxxxxxxx) (for backward compatibility)
 * @param phone Phone number to format
 * @returns Formatted phone number in E.164 format or original if invalid
 */
export const formatIndianPhone = (phone: string): string => {
  if (!phone) return "";
  
  // Clean the phone number from spaces, dashes, etc.
  const cleanedPhone = phone
    .replace(/\s+/g, "")
    .replace(/-/g, "")
    .replace(/\(/g, "")
    .replace(/\)/g, "");

  // Already in E.164 format
  if (/^\+91[6-9]\d{9}$/.test(cleanedPhone)) {
    return cleanedPhone;
  }

  // Has country code without +
  if (/^91[6-9]\d{9}$/.test(cleanedPhone)) {
    return `+${cleanedPhone}`;
  }

  // Just the 10 digit number
  if (/^[6-9]\d{9}$/.test(cleanedPhone)) {
    return `+91${cleanedPhone}`;
  }

  // Invalid format, return original
  return phone;
};
